import {
  Component,
  ChangeDetectorRef,
  DestroyRef,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  calibrationActions,
  selectCalibrationMeasurementsModalData,
  selectCalibrationMeasurementsModalLoading,
} from '@core-app/state/calibration';
import { SearchState } from '@core-app/state/search/search.reducer';
import { ComponentDataState } from '@enums';
import { TableColumn } from '@frontend-workspace/shared/src/lib/components/table/table.component';
import { ModalKey } from '@frontend-workspace/shared/src/lib/types/modal-key';
import { CalibrationMeasurement, Measurement } from '@interfaces';

import { Store } from '@ngrx/store';

@Component({
  selector: 'fip-measurements-modal',
  templateUrl: './calibration-measurements.modal.html',
  styleUrls: ['./calibration-measurements.modal.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CalibrationMeasurementsModalComponent implements OnInit {
  data = inject<{
    orderId: number;
    articleId: string;
    batchId: string;
    orderPositionId: string;
  }>(MAT_DIALOG_DATA);

  readonly modalKey: ModalKey = 'calibration-measurements';
  private readonly _changeDetector = inject(ChangeDetectorRef);
  private readonly _store = inject(Store<SearchState>);
  private readonly _destroyRef = inject(DestroyRef);

  modalData$ = this._store.select(selectCalibrationMeasurementsModalData());
  dataState: ComponentDataState = ComponentDataState.Loading;
  ComponentDataState = ComponentDataState;
  loading$ = this._store.select(selectCalibrationMeasurementsModalLoading());
  tableData: CalibrationMeasurement[] = [];

  columns: TableColumn<CalibrationMeasurement>[] = [
    {
      index: 'characteristicId',
      label: 'Characteristic ID',
      key: 'characteristicId',
    },
    {
      index: 'characteristicName',
      label: 'Characteristic Name',
      key: 'characteristicName',
    },
    {
      index: 'characteristicType',
      label: 'Characteristic Type',
      key: 'characteristicType',
    },
    {
      index: 'characteristicDecision',
      label: 'Characteristic Decision',
      key: 'characteristicDecision',
    },
    {
      index: 'measureUnit',
      label: 'Measure Unit',
      key: 'measureUnit',
    },
    {
      index: 'shouldValue',
      label: 'Should Value',
      key: 'shouldValue',
    },
    {
      index: 'lowerLimit',
      label: 'Lower Limit',
      key: 'lowerLimit',
    },
    {
      index: 'upperLimit',
      label: 'Upper Limit',
      key: 'upperLimit',
    },
    {
      index: 'inspectionEquipmentType',
      label: 'Inspection Equipment Type',
      key: 'inspectionEquipmentType',
    },
    {
      index: 'orderCharacteristicId',
      label: 'Order Characteristic ID',
      key: 'orderCharacteristicId',
    },
    {
      index: 'measureDate',
      label: 'Measure Date',
      key: 'measureDate',
    },
    {
      index: 'inspectionEquipmentId',
      label: 'Inspection Equipment ID',
      key: 'inspectionEquipmentId',
    },
  ];

  displayedColumns: (keyof CalibrationMeasurement)[] = [
    'characteristicId',
    'characteristicName',
    'characteristicDecision',
    'measureUnit',
    'shouldValue',
    'lowerLimit',
    'upperLimit',
    'inspectionEquipmentType',
    'measureDate',
    'inspectionEquipmentId',
  ];

  xTicksVals: number[] = [];
  xTicksLabels: string[] = [];
  yAxisLabel = 'Value';

  ngOnInit(): void {
    const { orderId, articleId, batchId, orderPositionId } = this.data;

    this._store.dispatch(
      calibrationActions.getMeasurementsData({
        orderId,
        articleId,
        batchId,
        orderPositionId,
      }),
    );

    this.modalData$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data) => {
        if (data) {
          this.tableData = data;
          this.setupData(data);
          this._changeDetector.markForCheck();
        }
      });
  }

  setupData(measurements: Measurement[] | CalibrationMeasurement[]) {
    this.setDataState(measurements);
  }

  setDataState(data: Measurement[] | CalibrationMeasurement[]) {
    if (data.length === 0) {
      this.dataState = ComponentDataState.NoData;
    } else {
      this.dataState = ComponentDataState.HasData;
    }
  }
}
